import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ToastrManager } from '@niceltd/sol/toastr';
import { cloneDeep } from 'lodash';
import { ReportType, SubscriptionRowNode } from 'src/app/admin/Subscription/subscription-management-model';
import { DashboardService } from 'src/app/dashboard/dashboard.service';
import { ModuleContainerService } from 'src/app/dashboard/module-container/module-container.service';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { ModuleSubscriptionService } from 'src/app/reusables/services/module-subscription.service';
import { SubscriptionSchedulerService } from 'src/app/reusables/subscription-scheduler/subscription-scheduler.service';
import { DashboardModuleResponsive } from 'src/shared/models/module-info.model';
import { ModuleManagerSetting } from 'src/shared/models/module-manager/module-manager-setting.model';
import { ICvSubscriptionScheduleData } from 'src/shared/models/subscriptions.model';

@Component({
    selector: 'cv-create-report-subscription',
    templateUrl: './create-report-subscription.component.html',
    styleUrls: ['./create-report-subscription.component.scss']
})
export class CreateReportSubscriptionComponent implements OnInit {
    @Input() startAtStep: number;
    @Input() editMode: boolean = false;
    @Input() editObject: SubscriptionRowNode;
    @Input() subscriptionGroups: string[];
    @Input() subscribableElements: DashboardModuleResponsive[] = [];

    // Returns saved schedule
    @Output() onSave = new EventEmitter<ICvSubscriptionScheduleData>();
    @Output() onClose = new EventEmitter();

    public currentStep: number = 1;
    public saving: boolean = false;
    public settingsLoading: boolean = false;
    public settingsLoaded: {
        generic: boolean;
        custom: boolean;
    } = {
            generic: false,
            custom: false
        };

    public validations: {
        subscriptionName: boolean;
        nameTooLong: boolean;
        selectedGroup: boolean;
        folderTooLong: boolean;
        selectedModule: boolean;
    };
    public stepValidated: boolean[];

    //Used to retrieve module settings when creating/editing subscriptions; if null, retrieve at step 2
    public subscribableModules: DashboardModuleResponsive[] = null;

    // Step 1 control models
    public subscriptionName: string = "";
    public selectedGroup: string = "";
    public groupKeys: any[] = [];
    public selectedModuleId: number = -1;
    public selectedModule: DashboardModuleResponsive = null;
    // Step 2 control models
    public validatingStep2 = false;
    public triggerApplySettings: EventEmitter<void> = new EventEmitter();
    // Step 3 control models
    public relatedId: number;
    public relatedKey: string = "";
    public reportType: ReportType = ReportType.Module;
    public startSaving: EventEmitter<void> = new EventEmitter();

    private _module = {
        key: null,
        data: null,
        options: {
            hasGenericSettings: true,
            hasMetricSelector: true,
            showSelectMetrics: false,
        },
    };
    private _customSettings: { [settingName: string]: ModuleManagerSetting<any> } = null;
    private _subscriptionDescription;

    constructor(
        private _msf: ModuleSubscriptionService,
        private _mcs: ModuleContainerService,
        private _dashboardService: DashboardService,
        private _subscriptionSchedulerService: SubscriptionSchedulerService,
        private _cvLocalizationService: CVLocalizationService,
        private _toastrManager: ToastrManager,
    ) {
        this.resetValidation();
    }

    async ngOnInit() {
        const promises = [
            await this._msf.getSubscribableModules(),
            await this._cvLocalizationService.GetSection('Subscriptions_Dashboard')
        ]

        await Promise.all(promises);
        this.subscribableModules = promises[0] as DashboardModuleResponsive[];
        if (!this.subscribableElements || this.subscribableElements.length == 0)
            this.subscribableElements = cloneDeep(this.subscribableModules);

        if (this.editMode == true) {
            if (!!this.editObject && typeof (this.editObject) == 'object') {
                this.editSetup();

                if (this.startAtStep > 0 && this.startAtStep < 4)
                    this.jumpToStep(this.startAtStep);
            }
            else
                console.error('[CreateReportSubscription] editObject must be defined when using edit mode.');
        }
        this.groupKeys = Object.keys(this.subscriptionGroups || {});
    }

    public navigateToStep(step: number) {
        if (step == this.currentStep)
            return;

        this.stepValidated[this.currentStep - 1] = true;

        // If previous steps have not been validated
        for (let i = 0; i < step - 1; ++i) {
            if (!this.stepValidated[i]) {
                this.stepValidated[this.currentStep - 1] = false;
                return;
            }
        }

        if (!this.validateCurrentStep()) {
            this.stepValidated[this.currentStep - 1] = false;
            return;
        }
        this.currentStep = step;

        if (this.currentStep == 2)
            this.getModuleForSubscription();
    }

    // Jump to a step without validation
    private jumpToStep(step: number) {
        if (step > 0 && step < 4)
            this.currentStep = step;
        if (this.currentStep == 2)
            this.getModuleForSubscription();
    }

    public async nextStep() {
        switch (this.currentStep) {
            case 1:
                await this.getModuleForSubscription();
                break;
            case 2:
                this.validatingStep2 = true;
                this.stepValidated[1] = false;
                this.triggerApplySettings.emit();
                break;
        }

        if (!this.validateCurrentStep())
            return;
        ++this.currentStep;
    }

    public onSettingsApplied(customSettings: { [settingName: string]: ModuleManagerSetting<any> }) {
        this._customSettings = customSettings;
        this.validatingStep2 = false;
        this.stepValidated[1] = true;
    }

    public onSettingsFailed(msg: string[]) {
        this.validatingStep2 = false;
        this.stepValidated[1] = false;
        if (msg != null)
            window["showErrorDialog"](msg[1], msg[0]);
    }

    private validateCurrentStep(): boolean {
        //Validate for the current step.
        //  Overwrites the validation value so that no assumptions are made.
        switch (this.currentStep) {
            case 1:
                this.validations.nameTooLong = (this.subscriptionName.length > 35);
                //Trim the subscription name first, to prevent whitespace-only group names.
                this.subscriptionName = this.subscriptionName.trim();
                this.validations.subscriptionName = (this.subscriptionName != '');

                this.validations.folderTooLong = (this.selectedGroup.length > 35);
                this.validations.selectedGroup = (this.selectedGroup != '');
                this.validations.selectedModule = (this.selectedModuleId != -1 && this.selectedModuleId != null);

                return (
                    this.validations.subscriptionName &&
                    this.validations.selectedGroup &&
                    this.validations.selectedModule &&
                    !this.validations.nameTooLong &&
                    !this.validations.folderTooLong
                );
            case (2):
                return !this.validatingStep2 && this.stepValidated[1];
            case (3):
                return true;
        }
    }

    public async onModuleSelectionChange() {
        this.selectedModule = this.subscribableElements.find((e) => e.ModuleID == this.selectedModuleId) ?? null;
    }

    private editSetup() {
        // Subscription name/group stuff
        this.subscriptionName = this.editObject.Name;
        this.selectedGroup = this.editObject.Folder;
        this.selectedModuleId = this.editObject.RelatedID;
        this.relatedId = this.editObject.RelatedID;
        this.relatedKey = this.editObject.RelatedKey;

        this.onModuleSelectionChange();

        //Match selected module from subscribable modules for localized name.
        this.editObject.RelatedName = this.subscribableElements.filter((e) => {
            return e.ModuleID == this.editObject.RelatedID;
        })[0].LocalizedName;
    }

    private async getModuleForSubscription() {
        if (this._module?.data != null || this._module.key != null || this.editMode) {
            this.mapExistingModuleProperties();
            this.setupModuleForSubscription();
        }

        this.settingsLoading = true;
        if (this.editMode && !this.selectedModuleId && this.editObject.RelatedID) {
            this.selectedModuleId = this.editObject.RelatedID;
        }

        if (this.relatedKey == "" || !this.relatedKey)
            this.relatedKey = await this._msf.getModuleForSubscription(this.selectedModuleId);
        this._module.key = this.relatedKey;
        this.relatedId = (typeof (this.selectedModuleId) == "string") ? parseInt(this.selectedModuleId) : this.selectedModuleId;

        this.setupModuleForSubscription();

    }

    private async setupModuleForSubscription() {
        if (this.subscribableModules == null)
            this.subscribableModules = await this._msf.getSubscribableModules();

        var matchedExistingModuleIndex = this.subscribableModules.map((s) => { return s.ModuleID; }).indexOf(this.relatedId);
        if (matchedExistingModuleIndex > -1 && this._dashboardService.directiveExists(this.subscribableModules[matchedExistingModuleIndex].ModuleSource)) {
            this.selectedModule = (this.subscribableModules[matchedExistingModuleIndex] != null) ? this.subscribableModules[matchedExistingModuleIndex] : null;
        }
        this.settingsLoading = false;
    }

    private mapExistingModuleProperties() {
        if (this.editMode) {
            this.relatedKey = this.editObject.RelatedKey;
            this.relatedId = this.editObject.RelatedID;
        }
    }

    public clickSave() {
        this.startSaving.emit();
    }

    public async onSaved(schedule: ICvSubscriptionScheduleData) {
        this.saving = true;

        //Add the properties set in this popup
        schedule.title = this.subscriptionName;
        schedule.description = this._subscriptionDescription;
        schedule.folder = this.selectedGroup;
        schedule.relatedID = this.relatedId;
        schedule.relatedKey = this.relatedKey;
        schedule.reportType = this.reportType;

        //The module settings (such as selected metrics) are saved when applyModuleGenericSettings is called.
        await this._msf.applyModuleGenericSettings(this.relatedId, this.relatedKey);

        if (this._customSettings) {
            var settingsToSave = {};
            for (var settingName in this._customSettings) {
                var setting = this._customSettings[settingName];
                try {
                    settingsToSave[settingName] = JSON.stringify(setting.getValue());
                } catch (ex) {
                    delete settingsToSave[settingName];
                }
            }
            this._mcs.saveModuleStates(this.relatedKey, settingsToSave);
        }

        if (this.editMode)
            await this._subscriptionSchedulerService.updateSubscriptionSchedule(schedule);
        else
            await this._subscriptionSchedulerService.createSubscriptionSchedule(schedule);

        this.resetValidation();
        this.onSave.emit(schedule);

        const toastString = this.editMode ?  "Subscription_Updated" : "Subscription_Created";
        this._toastrManager.showToaster('success',
            this._cvLocalizationService.getCachedText("Subscriptions_Dashboard", toastString));
    }

    public async clickCancel() {
        this.saving = false;

        //Control models
        this.subscriptionName = '';
        this._subscriptionDescription = '';
        this.selectedGroup = '';
        this.selectedModuleId = -1;

        this.resetValidation();

        //Delete the module
        if (!this.editMode)
            await this._msf.deleteSubscriptionModule(this._module.key);
        else
            await this._mcs.cancelGenericSettings(this.relatedId, this.relatedKey + '_temp');

        //Reset the selected module, close the modal.
        this._module = {
            key: null,
            data: null,
            options: {
                hasGenericSettings: true,
                hasMetricSelector: true,
                showSelectMetrics: false,
            },
        };
        this.onClose.emit(null);
    }

    private resetValidation() {
        this.stepValidated = [false, false, false];

        this.validations = {
            //Step 1
            subscriptionName: true,
            nameTooLong: false,
            selectedGroup: true,
            folderTooLong: false,
            selectedModule: true,
        };
    }
}
